import {
  AGTable,
  Button,
  ButtonMode,
  ButtonSize,
  CircleBar,
  colors,
  DataLoadError,
  Divider,
  Icon,
  ICONS,
  Loader,
  Tooltip,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { GridApi, DomLayoutType } from '@ag-grid-community/core';
import { ROUTES } from '../../Base.constants';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import { FundamentalAttExtended, FundamentalAttr } from '../../models/FundamentalMetrics';
import {
  calculatedValuesColumns,
  getRPCalculatorColumns,
  RPCalculatorAutoGroupColDef,
  RPCalculatorGridOptions,
} from './RPCalculator.constants';
import {
  canAccess,
  convertToPercentage,
  getCqsColor,
  mapCalculatedFundamentalsToTable,
  prepareDisplayAttributesForCalculation,
} from './RPCalculator.logic';
import styles from './RPCalculator.module.scss';
import useCalculateFundamentals from '../../hooks/riskProducts/useCalculateFundamentals';
import ErrorContainer from './components/Error/Error';
import { CalculatedFundamentals, CalculatedFundamentalsTable } from '../../models/CalculatedFundamentals';
import useFetchFundamentals from '../../hooks/riskProducts/useFetchFundamentals';

const cx = classNames.bind(styles);

const CalculatorTable = AGTable<FundamentalAttExtended>;
const CalculatedValuesTable = AGTable<CalculatedFundamentalsTable>;

const renderGroupRow = (params) => (
  <span className={cx('group-header', 'ag-cell-wrapper')}>
    <span>{params.value}</span>
  </span>
);

const RPCalculator = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { data: userPermissions, isLoading: permissionsLoading } = useFetchUserPermissions();
  const gridApi = useRef<GridApi | null>(null);
  const [initialCalculation, setInitialCalculation] = useState<CalculatedFundamentals>();

  const companyId = useMemo(() => searchParams.get('companyId'), [searchParams]);
  const { data: fundamental, isLoading, isError, refetch } = useFetchFundamentals(companyId);
  const { cqs, displayName, crl, cre, displayAttributes, currency, modelSector } = fundamental || {};
  const {
    mutate: calculateFundamentals,
    isError: calculationError,
    data: calculatedFundamentals,
    reset: clearCalculation,
    isLoading: calculationLoading,
  } = useCalculateFundamentals();
  const columns = useMemo(() => getRPCalculatorColumns(currency), [currency]);
  const gridOptions = useMemo(
    () => ({
      ...RPCalculatorGridOptions,
      groupRowRenderer: renderGroupRow,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      domLayout: 'autoHeight' as DomLayoutType,
    }),
    []
  );

  const renderMetric = (label, value, originalValue) => (
    <div className={cx('metric')} data-testid={`${label}-metric`}>
      <span className={cx('header')}>{label}</span>
      <div className={cx('value')}>
        <Icon name={ICONS.CaretArrowUp} width={16} height={16} color={colors.tertiary400} />
        {value ?? originalValue}
        <Icon name={ICONS.CaretArrowDown} width={16} height={16} color={colors.tertiary400} />
      </div>
      <div className={cx('footer')}>
        <span className={cx('title')}>Original</span>
        <span className={cx('original-value')}>{originalValue}</span>
      </div>
    </div>
  );

  const renderCircleMetric = (label, value, originalValue) => (
    <div className={cx('metric')} data-testid={`${label}-metric`}>
      <span className={cx('header')}>{label}</span>
      <div className={cx('circular-value')}>
        <CircleBar
          value={value ?? originalValue}
          color={getCqsColor(value ?? originalValue)}
          label={value ?? originalValue}
        />
      </div>
      <div className={cx('footer')}>
        <span className={cx('title')}>Original</span>
        <span className={cx('original-value')}>{originalValue}</span>
      </div>
    </div>
  );

  const onGridReady = (params) => {
    gridApi.current = params.api;
    focusOnFirstField();
  };

  const focusOnFirstField = () => {
    gridApi.current?.startEditingCell({
      rowIndex: 1,
      colKey: 'modifiedValue',
    });
  };

  const resetGridData = () => {
    if (gridApi.current && displayAttributes) {
      gridApi.current.setRowData(
        displayAttributes.map((i) => {
          return {
            ...i,
            modifiedValue: undefined,
          };
        })
      );
    }
  };

  const reset = () => {
    resetGridData();
    clearCalculation();
    focusOnFirstField();
  };

  const calculate = () => {
    const gridData = gridApi?.current
      ? (gridApi?.current
          ?.getRenderedNodes()
          ?.map((node) => node?.data)
          .filter((i) => i) as unknown as FundamentalAttr[] | undefined)
      : displayAttributes;

    if (gridData) {
      const data = prepareDisplayAttributesForCalculation(gridData);
      calculateFundamentals({
        cs_companyid: parseInt(companyId!, 10),
        model_sector: modelSector!,
        display_attributes: data,
      });
    }
  };

  useEffect(() => {
    if (displayAttributes) {
      calculate();
    }
  }, [displayAttributes]);

  useEffect(() => {
    if (!initialCalculation && calculatedFundamentals) {
      const adjustedCalculatedFundamentals = {
        pct: {},
        calculated_data: {},
        original_values: calculatedFundamentals?.original_values,
      } as CalculatedFundamentals;
      setInitialCalculation(adjustedCalculatedFundamentals);
      clearCalculation();
    }
  }, [calculatedFundamentals]);

  if (!companyId) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  if (isLoading || permissionsLoading || !initialCalculation) {
    return <Loader className="loader-container" />;
  }

  const hasAccess = canAccess(userPermissions!, fundamental!);
  if (!hasAccess) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  if (isError) {
    return (
      <div className={cx('body')}>
        <DataLoadError onRetryClick={refetch} />
      </div>
    );
  }

  return (
    <div className={cx('body')}>
      <div className={cx('header')}>
        <div className={cx('side')}>
          {/*  <ActionButton
            icon={ICONS.CaretLeft}
            className={cx('action-item')}
            onClick={() => {}}
            tooltip="Back"
            dataTestId="back-button"
            small
            secondary
            dark
          /> */}
          <Tooltip content={displayName}>
            <div className={cx('title')}>{displayName}</div>
          </Tooltip>
        </div>
        {/* <ActionButton
          icon={ICONS.Download}
          onClick={() => {}}
          tooltip="Export"
          className={cx('action')}
          dataTestId="export-button"
          small
          secondary
          dark
          size={12}
        >
          Export
        </ActionButton> */}
      </div>
      <div className={cx('content')}>
        {calculationError && (
          <ErrorContainer
            error={
              <>
                Error loading data,{' '}
                <span className={cx('error-link')} onClick={() => calculate()}>
                  try again
                </span>
              </>
            }
          />
        )}
        <div className={cx('metrics')}>
          {renderMetric(
            'CRE',
            calculatedFundamentals?.calculated_data.cre
              ? `${convertToPercentage(calculatedFundamentals?.calculated_data.cre!, 2)}%`
              : `${convertToPercentage(cre!, 2)}%`,
            `${convertToPercentage(cre!, 2)}%`
          )}
          <Divider vertical />
          {renderCircleMetric('CQS', calculatedFundamentals?.calculated_data.cqs, cqs)}
          <Divider vertical />
          {renderMetric('CRL', calculatedFundamentals?.calculated_data.credit_risk_level, crl)}
        </div>
        {/* <div className={cx('actions')}>
          <div className={cx('side')}>
            <Button mode={ButtonMode.PRIMARY} size={ButtonSize.SMALL}>
              Scenario 1
            </Button>
            <Button mode={ButtonMode.SECONDARY} size={ButtonSize.SMALL}>
              <Icon name={ICONS.Plus} width={12} height={12} color={colors.primary800} />
            </Button>
          </div>
          <Button className={cx('plain')} mode={ButtonMode.PLAIN} disabled>
            Reset
          </Button>
        </div> */}
        <CalculatorTable
          rowData={displayAttributes}
          columnDefs={columns}
          gridOptions={gridOptions}
          autoGroupColumnDef={RPCalculatorAutoGroupColDef}
          suppressMovableColumns
          onGridReady={onGridReady}
          rowModelType="clientSide"
          onRowDataUpdated={() => focusOnFirstField()}
        />
        <div>
          <span className={cx('group-header', 'ag-cell-wrapper', 'calculated-values-header')}>Calculated Values</span>
          <CalculatedValuesTable
            columnDefs={calculatedValuesColumns}
            rowData={mapCalculatedFundamentalsToTable(calculatedFundamentals ?? initialCalculation)}
            gridOptions={{
              domLayout: 'autoHeight' as DomLayoutType,
            }}
          />
        </div>
        <div className={cx('bottomToolbar')}>
          <Button
            mode={ButtonMode.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={() => reset()}
            disabled={!calculatedFundamentals}
          >
            Reset
          </Button>
          <Button
            mode={ButtonMode.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={() => calculate()}
            loading={calculationLoading}
          >
            Calculate
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RPCalculator;
